import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import {
  CONTENT_CATEGORY_LABELS,
  SUITABLE_GRADE_LABELS,
  type ContentCategory,
  type SuitableGrade,
} from '@shared/api.interface';
import { MobileFilterDrawer } from './MobileFilterDrawer';
import { FilterTags } from './FilterTags';

interface SearchFilterProps {
  search: string;
  category: ContentCategory | '';
  grade: SuitableGrade | '';
  resultCount: number;
  onSearchChange: (value: string) => void;
  onCategoryChange: (value: ContentCategory | '') => void;
  onGradeChange: (value: SuitableGrade | '') => void;
  onReset: () => void;
}

export function SearchFilter({
  search,
  category,
  grade,
  resultCount,
  onSearchChange,
  onCategoryChange,
  onGradeChange,
  onReset,
}: SearchFilterProps) {
  const [localSearch, setLocalSearch] = useState(search);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleSearch = () => {
    onSearchChange(localSearch);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleReset = () => {
    setLocalSearch('');
    onReset();
  };

  const hasFilters = search || category || grade;

  return (
    <div className="space-y-4" data-ai-section-type="card-stat">
      {/* 移动端布局 */}
      <div className="md:hidden space-y-3">
        {/* 搜索框 + 筛选按钮 */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="搜索主题名称或文案..."
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              className="pl-9"
            />
          </div>
          <Button
            variant={hasFilters ? 'default' : 'outline'}
            size="icon"
            onClick={() => setDrawerOpen(true)}
          >
            <SlidersHorizontal className="h-4 w-4" />
          </Button>
        </div>

        {/* 快捷标签 */}
        <FilterTags
          category={category}
          onCategoryChange={onCategoryChange}
        />

        {/* 移动端筛选抽屉 */}
        <MobileFilterDrawer
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          category={category}
          grade={grade}
          resultCount={resultCount}
          onCategoryChange={onCategoryChange}
          onGradeChange={onGradeChange}
          onReset={handleReset}
          onConfirm={() => setDrawerOpen(false)}
        />
      </div>

      {/* 桌面端布局 */}
      <div className="hidden md:block space-y-4">
        {/* 搜索框 */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="搜索主题名称或文案..."
              value={localSearch}
              onChange={(e) => setLocalSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              className="pl-9"
            />
          </div>
          <Button onClick={handleSearch}>搜索</Button>
          {hasFilters && (
            <Button variant="outline" onClick={handleReset} className="gap-1">
              <X className="h-4 w-4" />
              重置
            </Button>
          )}
        </div>

        {/* 筛选器 */}
        <div className="flex flex-wrap gap-3">
          {/* 内容分类标签组 */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant={category === '' ? 'default' : 'outline'}
              size="sm"
              onClick={() => onCategoryChange('')}
              className="rounded-full"
            >
              全部
            </Button>
            {Object.entries(CONTENT_CATEGORY_LABELS).map(([key, label]) => (
              <Button
                key={key}
                variant={category === key ? 'default' : 'outline'}
                size="sm"
                onClick={() => onCategoryChange(key as ContentCategory)}
                className="rounded-full"
              >
                {label}
              </Button>
            ))}
          </div>

          {/* 年级筛选 */}
          <Select
            value={grade}
            onValueChange={(value) => onGradeChange(value as SuitableGrade | '')}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="选择年级" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">全部年级</SelectItem>
              <SelectItem value="low">{SUITABLE_GRADE_LABELS.low}</SelectItem>
              <SelectItem value="high">{SUITABLE_GRADE_LABELS.high}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}
